import { useState, useEffect } from "react";
import {
  DAYS, DEFAULT_PERIODS, ROLE_PRESETS,
  useIsMobile, createEmployee,
  StepIndicator, NavButtons, GLOBAL_STYLES,
} from "./onboarding/shared";
import Step1RestaurantInfo from "./onboarding/Step1RestaurantInfo";
import Step2Hours from "./onboarding/Step2Hours";
import Step3RolesStaffing from "./onboarding/Step3RolesStaffing";
import Step4Employees from "./onboarding/Step4Employees";
import Step5Generate from "./onboarding/Step5Generate";
import { supabase } from "./supabase";
import { runScheduleGeneration } from "./scheduleGenerator";

export default function OnboardingWizard({ companyId, onComplete }) {
  const isMobile = useIsMobile();
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState("forward");
  const [transitioning, setTransitioning] = useState(false);

  const [data, setData] = useState({
    restaurant: { name: "", manager: "", address: "", city: "", state: "", zip: "", phone: "" },
    dayData: DAYS.map(() => ({ isOpen: true, open: "11:00", close: "22:00", periods: JSON.parse(JSON.stringify(DEFAULT_PERIODS)) })),
    roles: ROLE_PRESETS.map(r => ({ ...r, active: false })),
    staffing: {},
    employees: [createEmployee()],
  });

  // Pre-fill restaurant name and manager from signup metadata
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      const meta = user?.user_metadata || {};
      if (meta.restaurant_name || meta.manager_name) {
        setData(prev => ({
          ...prev,
          restaurant: {
            ...prev.restaurant,
            name: prev.restaurant.name || meta.restaurant_name || "",
            manager: prev.restaurant.manager || meta.manager_name || "",
          },
        }));
      }
    });
  }, []);

  const canNext = [
    () => data.restaurant.name.length > 0 && data.restaurant.manager.length > 0,
    () => data.dayData.some(d => d.isOpen),
    () => data.roles.some(r => r.active) && Object.values(data.staffing).some(v => v > 0),
    () => data.employees.some(e => e.name && e.role),
    () => true,
  ];

  const goTo = (target) => {
    if (transitioning) return;
    setDirection(target > step ? "forward" : "backward");
    setTransitioning(true);
    setTimeout(() => {
      setStep(target);
      setTimeout(() => setTransitioning(false), 50);
    }, 200);
  };

  const next = () => { if (step < 4 && canNext[step]()) goTo(step + 1); };
  const back = () => { if (step > 0) goTo(step - 1); };

  const writeDataToSupabase = async () => {
    const dayMap = { 0: 'Mon', 1: 'Tue', 2: 'Wed', 3: 'Thu', 4: 'Fri', 5: 'Sat', 6: 'Sun' };

    // 1. Update company info
    const { getRegionFromZip } = await import('./zipToRegion');
    const region = getRegionFromZip(data.restaurant.zip, data.restaurant.state);
    await supabase.from('companies').update({
      name: data.restaurant.name,
      manager_name: data.restaurant.manager,
      address: data.restaurant.address,
      city: data.restaurant.city,
      state: data.restaurant.state,
      zip: data.restaurant.zip,
      phone: data.restaurant.phone,
      zip_code: data.restaurant.zip,
      region: region,
    }).eq('id', companyId);

    // 2. Write restaurant_hours
    const hoursRows = DAYS.map((day, i) => ({
      company_id: companyId,
      day_of_week: day,
      is_open: data.dayData[i].isOpen,
      open_time: data.dayData[i].open,
      close_time: data.dayData[i].close,
    }));
    await supabase.from('restaurant_hours').delete().eq('company_id', companyId);
    await supabase.from('restaurant_hours').insert(hoursRows);

    // 3. Write service_templates
    const templateRows = [];
    DAYS.forEach((day, i) => {
      const d = data.dayData[i];
      Object.entries(d.periods).forEach(([period, config]) => {
        templateRows.push({
          company_id: companyId,
          day_of_week: day,
          meal_period: period,
          is_active: d.isOpen && config.active,
        });
      });
    });
    await supabase.from('service_templates').delete().eq('company_id', companyId);
    await supabase.from('service_templates').insert(templateRows);

    // 4. Write roles
    const activeRoles = data.roles.filter(r => r.active);
    await supabase.from('roles').delete().eq('company_id', companyId);
    await supabase.from('roles').insert(activeRoles.map((r, i) => ({
      company_id: companyId,
      name: r.name,
      type: r.type,
      color: `bg-${r.name.toLowerCase().replace(' ', '-')}-100 text-${r.name.toLowerCase().replace(' ', '-')}-700`,
      sort_order: i,
    })));

    // 5. Write staffing_levels
    const staffRows = [];
    Object.entries(data.staffing).forEach(([key, count]) => {
      if (count > 0) {
        const [dayIndex, period, ...roleParts] = key.split('-');
        const role = roleParts.join('-');
        staffRows.push({
          company_id: companyId,
          day_of_week: dayMap[parseInt(dayIndex)],
          meal_period: period,
          role: role,
          required_count: count,
        });
      }
    });
    await supabase.from('staffing_levels').delete().eq('company_id', companyId);
    if (staffRows.length > 0) await supabase.from('staffing_levels').insert(staffRows);

    // 6. Write employees
    const empRows = data.employees
      .filter(e => e.name)
      .map(e => ({
        company_id: companyId,
        name: e.name,
        initials: e.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2),
        phone: e.phone || null,
        role: e.role || null,
        availability: (e.availability || []).join(','),
        color: 'bg-blue-100 text-blue-700',
        max_hours_per_week: 40,
        target_hours: e.role === 'Manager' ? 40 : 30,
        status: 'active',
      }));
    await supabase.from('employees').insert(empRows);
  };

  // "Build Schedule Now" — write data, generate schedule, return results to Step5
  const handleBuildSchedule = async (action) => {
    // If called from success view "View Schedule" button, just navigate
    if (action === 'viewSchedule') {
      onComplete('dashboard');
      return;
    }
    // Write all wizard data to Supabase
    await writeDataToSupabase();
    // Run real schedule generation
    const result = await runScheduleGeneration(companyId);
    return result;
  };

  // "Fine-Tune Team First" — write data, navigate to employees page
  const handleFineTune = async () => {
    await writeDataToSupabase();
    onComplete('employees');
  };

  const anim = transitioning
    ? { opacity: 0, transform: direction === "forward" ? "translateX(-30px)" : "translateX(30px)", transition: "all 0.2s ease-out" }
    : { opacity: 1, transform: "translateX(0)", transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)" };

  return (
    <div style={{ minHeight: "100vh", background: "#F5F5F4", fontFamily: "'DM Sans', sans-serif", padding: isMobile ? "20px 16px 100px" : "32px 24px" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap" rel="stylesheet" />
      <style>{GLOBAL_STYLES}</style>

      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <StepIndicator current={step} isMobile={isMobile} />

        <div style={anim}>
          {step === 0 && <Step1RestaurantInfo data={data} setData={setData} isMobile={isMobile} companyId={companyId} />}
          {step === 1 && <Step2Hours data={data} setData={setData} isMobile={isMobile} companyId={companyId} />}
          {step === 2 && <Step3RolesStaffing data={data} setData={setData} isMobile={isMobile} companyId={companyId} />}
          {step === 3 && <Step4Employees data={data} setData={setData} isMobile={isMobile} companyId={companyId} />}
          {step === 4 && <Step5Generate data={data} isMobile={isMobile} companyId={companyId} onGoToStep={goTo} onBuildSchedule={handleBuildSchedule} onFineTune={handleFineTune} />}
        </div>

        {step < 4 && (
          <div style={{ maxWidth: step === 0 ? 560 : step === 3 ? (isMobile ? 560 : 780) : 1000, margin: "0 auto" }}>
            <NavButtons onBack={back} onNext={next} canNext={canNext[step]()} isMobile={isMobile} showBack={step > 0} />
          </div>
        )}
      </div>
    </div>
  );
}
